import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { HelpCircle, BookOpen, Play, Sparkles, Mail } from 'lucide-react';
import { TOUR_START_EVENT } from './AdminGuidedTour';
import { WHATS_NEW_OPEN_EVENT } from './WhatsNewModal';
import api from '../utils/api';

/**
 * HelpMenu
 *
 * Admin-facing help dropdown. Ships four entry points:
 *  - Restart guided tour — dispatches `ievntur:start-tour` (tour is
 *    otherwise never shown; users explicitly asked to disable the
 *    auto-popup on every login / app re-focus).
 *  - Open "What's New" modal (clears its dismissed flag)
 *  - Docs page link (static route — single-page FAQ)
 *  - Contact support (mailto)
 */
const HelpMenu = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);

  // Each role has its own Docs destination so the single dropdown works
  // on the admin, talent, and client headers without 403s.
  const docsPath = (() => {
    const role = user?.role;
    if (role === 'admin' || role === 'super_admin') return '/admin/help';
    if (role === 'talent') return '/talent/help';
    if (role === 'client') return '/client/help';
    return '/admin/help';
  })();

  const restartTour = () => {
    setOpen(false);
    // Fire the manual-start event; the RoleGuidedTour component is mounted
    // globally in App.js and listens for it. No full-page reload required.
    window.dispatchEvent(new CustomEvent(TOUR_START_EVENT));
  };

  const replayWhatsNew = async () => {
    // Iter-234 — server-backed reset for talent + client. For other roles
    // (admin/agency) we still clear the legacy localStorage flag so old
    // release modals can re-show after a deploy.
    const role = user?.role;
    if (role === 'talent' || role === 'client') {
      try {
        await api.post('/whats-new/reset');
      } catch { /* ignore */ }
      setOpen(false);
      window.dispatchEvent(new CustomEvent(WHATS_NEW_OPEN_EVENT));
      return;
    }
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith('ievntur_whats_new_seen_')) localStorage.removeItem(k);
    });
    setOpen(false);
    navigate(0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="px-2" data-testid="help-menu-btn" title="Help">
          <HelpCircle className="w-5 h-5 sm:mr-1" />
          <span className="hidden sm:inline">Help</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1 bg-white" align="end" data-testid="help-menu-panel">
        <button
          type="button"
          onClick={restartTour}
          className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded hover:bg-gray-100 text-left"
          data-testid="help-restart-tour"
        >
          <Play className="w-4 h-4 text-cyan-600" /> Restart guided tour
        </button>
        <button
          type="button"
          onClick={replayWhatsNew}
          className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded hover:bg-gray-100 text-left"
          data-testid="help-whats-new"
        >
          <Sparkles className="w-4 h-4 text-violet-600" /> What's new
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); navigate(docsPath); }}
          className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded hover:bg-gray-100 text-left"
          data-testid="help-docs"
        >
          <BookOpen className="w-4 h-4 text-gray-600" /> Docs & FAQs
        </button>
        <a
          href="mailto:support@ievntur.com"
          className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded hover:bg-gray-100"
          data-testid="help-contact-support"
        >
          <Mail className="w-4 h-4 text-gray-600" /> Contact support
        </a>
      </PopoverContent>
    </Popover>
  );
};

export default HelpMenu;
